import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import DataTable, { Column } from '../components/ui/DataTable';

// Policy definitions grouped by module -- maps config key to display info
const POLICY_GROUPS: { header: string; policies: { key: string; label: string; type: 'NUMERIC' | 'STRING' | 'JSON'; unit?: string; description: string }[] }[] = [
  {
    header: 'AP / Procure-to-Pay',
    policies: [
      { key: 'AP_PRICE_TOLERANCE_PCT', label: 'Price Match Tolerance', type: 'NUMERIC', unit: '%', description: '3-way match price variance threshold' },
      { key: 'AP_QTY_TOLERANCE_PCT', label: 'Quantity Match Tolerance', type: 'NUMERIC', unit: '%', description: '3-way match quantity variance threshold' },
      { key: 'AP_AUTO_APPROVE_MAX', label: 'AP Auto-Approve Limit', type: 'NUMERIC', unit: '$', description: 'Auto-approve matched invoices below this amount' },
      { key: 'PO_AUTO_APPROVE_MAX', label: 'PO Auto-Approve Limit', type: 'NUMERIC', unit: '$', description: 'Auto-approve POs below this amount' },
      { key: 'PO_PROCUREMENT_MAX', label: 'PO Procurement Tier', type: 'NUMERIC', unit: '$', description: 'POs up to this amount -- procurement manager approval' },
      { key: 'PO_DIRECTOR_MAX', label: 'PO Director Tier', type: 'NUMERIC', unit: '$', description: 'POs up to this amount -- director approval; above goes to CFO' },
      { key: 'CAPEX_THRESHOLD', label: 'Capitalization Threshold', type: 'NUMERIC', unit: '$', description: 'Expense vs. capital asset threshold' },
      { key: 'VENDOR_EVAL_FREQUENCY_MONTHS', label: 'Vendor Evaluation Frequency', type: 'NUMERIC', unit: 'months', description: 'How often to evaluate vendor performance' },
    ],
  },
  {
    header: 'AR / Order-to-Cash',
    policies: [
      { key: 'AR_LOSS_RATE_CURRENT', label: 'Loss Rate -- Current', type: 'NUMERIC', unit: '%', description: 'Expected loss rate for current receivables' },
      { key: 'AR_LOSS_RATE_1_30', label: 'Loss Rate -- 1-30 Days', type: 'NUMERIC', unit: '%', description: 'Expected loss rate for 1-30 days overdue' },
      { key: 'AR_LOSS_RATE_31_60', label: 'Loss Rate -- 31-60 Days', type: 'NUMERIC', unit: '%', description: 'Expected loss rate for 31-60 days overdue' },
      { key: 'AR_LOSS_RATE_61_90', label: 'Loss Rate -- 61-90 Days', type: 'NUMERIC', unit: '%', description: 'Expected loss rate for 61-90 days overdue' },
      { key: 'AR_LOSS_RATE_OVER_90', label: 'Loss Rate -- Over 90 Days', type: 'NUMERIC', unit: '%', description: 'Expected loss rate for 90+ days overdue' },
      { key: 'AR_WRITE_OFF_APPROVAL_THRESHOLD', label: 'Write-Off Approval Threshold', type: 'NUMERIC', unit: '$', description: 'Write-offs above this require CFO approval' },
      { key: 'CREDIT_RISK_WEIGHT_PAYMENT_HISTORY', label: 'Credit Risk -- Payment History Weight', type: 'NUMERIC', description: 'Weight for payment history in credit score (0-1)' },
      { key: 'CREDIT_RISK_WEIGHT_UTILIZATION', label: 'Credit Risk -- Utilization Weight', type: 'NUMERIC', description: 'Weight for credit utilization (0-1)' },
      { key: 'CREDIT_RISK_WEIGHT_AGING', label: 'Credit Risk -- Aging Weight', type: 'NUMERIC', description: 'Weight for AR aging (0-1)' },
      { key: 'CREDIT_RISK_WEIGHT_ORDER_TREND', label: 'Credit Risk -- Order Trend Weight', type: 'NUMERIC', description: 'Weight for order trend (0-1)' },
      { key: 'CREDIT_RISK_WEIGHT_INDUSTRY', label: 'Credit Risk -- Industry Weight', type: 'NUMERIC', description: 'Weight for industry risk (0-1)' },
    ],
  },
  {
    header: 'Expense Management',
    policies: [
      { key: 'EXPENSE_AUTO_APPROVE_MAX', label: 'Expense Auto-Approve Limit', type: 'NUMERIC', unit: '$', description: 'Auto-approve expense reports below this with zero violations' },
    ],
  },
  {
    header: 'Treasury & Close',
    policies: [
      { key: 'BANK_RECON_TOLERANCE_PCT', label: 'Bank Recon Tolerance', type: 'NUMERIC', unit: '%', description: 'Fuzzy match amount tolerance for bank reconciliation' },
      { key: 'BANK_RECON_TOLERANCE_DAYS', label: 'Bank Recon Date Tolerance', type: 'NUMERIC', unit: 'days', description: 'Fuzzy match date tolerance for bank reconciliation' },
      { key: 'FX_EXPOSURE_LIMIT', label: 'FX Exposure Limit', type: 'NUMERIC', unit: '$', description: 'Maximum net FX exposure per currency before alert' },
      { key: 'FORECAST_HORIZON_WEEKS', label: 'Cash Forecast Horizon', type: 'NUMERIC', unit: 'weeks', description: 'Rolling cash forecast horizon' },
      { key: 'RECON_FREQUENCY_DAYS', label: 'Reconciliation Frequency', type: 'NUMERIC', unit: 'days', description: 'How often to run bank reconciliation' },
      { key: 'CLOSE_TARGET_DAY', label: 'Close Target Day', type: 'NUMERIC', unit: 'day of month', description: 'Target day for period-end close' },
      { key: 'SLA_RESPONSE_HOURS', label: 'SLA Response Time', type: 'NUMERIC', unit: 'hours', description: 'Maximum hours for approval response' },
      { key: 'ESCALATION_AFTER_HOURS', label: 'Auto-Escalation After', type: 'NUMERIC', unit: 'hours', description: 'Auto-escalate pending approvals after this time' },
    ],
  },
  {
    header: 'DOA Levels',
    policies: [
      { key: 'DOA_L2', label: 'L2 -- Analyst Limit', type: 'NUMERIC', unit: '$', description: 'Maximum approval authority for Analyst level' },
      { key: 'DOA_L3', label: 'L3 -- Supervisor Limit', type: 'NUMERIC', unit: '$', description: 'Maximum approval authority for Supervisor level' },
      { key: 'DOA_L4', label: 'L4 -- Manager Limit', type: 'NUMERIC', unit: '$', description: 'Maximum approval authority for Manager level' },
      { key: 'DOA_L5', label: 'L5 -- Director Limit', type: 'NUMERIC', unit: '$', description: 'Maximum approval authority for Director level' },
      { key: 'DOA_L6', label: 'L6 -- VP/CFO Limit', type: 'NUMERIC', unit: '$', description: 'Maximum approval authority for VP/CFO level' },
      { key: 'DOA_L7', label: 'L7 -- Board / Admin', type: 'NUMERIC', unit: '$', description: 'Top tier; leave at the unlimited sentinel (999999999999) unless your board imposes a ceiling' },
    ],
  },
  {
    header: 'Governance',
    policies: [
      { key: 'MATERIALITY_THRESHOLD', label: 'Materiality Threshold', type: 'NUMERIC', unit: '$', description: 'Exceptions below this are auto-resolved' },
      { key: 'AUTO_APPROVAL_CEILING', label: 'Auto-Approval Ceiling', type: 'NUMERIC', unit: '$', description: 'Maximum amount for auto-approval (any type)' },
      { key: 'DEFAULT_FRAMEWORK', label: 'Accounting Framework', type: 'STRING', description: 'US_GAAP, IFRS, or dual reporting' },
      { key: 'PRIMARY_CURRENCY', label: 'Reporting Currency', type: 'STRING', description: 'Primary reporting currency' },
      { key: 'PRIMARY_ERP', label: 'Primary ERP System', type: 'STRING', description: 'Connected ERP (SAP, Oracle, NetSuite, etc.)' },
      { key: 'FISCAL_YEAR_START_MONTH', label: 'Fiscal Year Start', type: 'NUMERIC', unit: 'month', description: 'Month number (1=Jan, 4=Apr, etc.)' },
    ],
  },
  {
    header: 'Contracts & Procurement',
    policies: [
      { key: 'CONTRACT_RENEWAL_NOTICE_DAYS', label: 'Renewal Notice Period', type: 'NUMERIC', unit: 'days', description: 'Days before expiry to flag for renewal' },
      { key: 'ASSET_DEPRECIATION_METHOD', label: 'Depreciation Method', type: 'STRING', description: 'STRAIGHT_LINE, DECLINING_BALANCE, or UNITS_OF_PRODUCTION' },
    ],
  },
  {
    header: 'HR & Legal',
    policies: [
      { key: 'MAX_ANNUAL_LEAVE_DAYS', label: 'Annual Leave Allowance', type: 'NUMERIC', unit: 'days', description: 'Maximum PTO days per year' },
      { key: 'PROBATION_PERIOD_MONTHS', label: 'Probation Period', type: 'NUMERIC', unit: 'months', description: 'New hire probation period' },
      { key: 'PERFORMANCE_REVIEW_FREQUENCY', label: 'Performance Review Cycle', type: 'STRING', description: 'ANNUAL, SEMI_ANNUAL, or QUARTERLY' },
      { key: 'LITIGATION_RESERVE_THRESHOLD', label: 'Litigation Reserve Threshold', type: 'NUMERIC', unit: '$', description: 'Minimum exposure to trigger reserve accrual' },
      { key: 'FILING_REMINDER_DAYS', label: 'Filing Reminder Lead Time', type: 'NUMERIC', unit: 'days', description: 'Days before deadline to send filing reminders' },
    ],
  },
  {
    header: 'Commercial',
    policies: [
      { key: 'QUOTE_EXPIRY_DAYS', label: 'Quote Validity Period', type: 'NUMERIC', unit: 'days', description: 'Default days before quote expires' },
      { key: 'COMMISSION_CLAWBACK_MONTHS', label: 'Commission Clawback Period', type: 'NUMERIC', unit: 'months', description: 'Months after deal close for clawback eligibility' },
      { key: 'PIPELINE_STAGE_COUNT', label: 'Pipeline Stages', type: 'NUMERIC', description: 'Number of stages in sales pipeline' },
    ],
  },
];

const API = `${API_V1}/org/configuration`;

const inputStyle: React.CSSProperties = {
  padding: '7px 12px', fontSize: 13,
  border: '1px solid var(--border-subtle)', borderRadius: 6,
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  outline: 'none',
};

export default function ClientPolicies() {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editType, setEditType] = useState<'NUMERIC' | 'STRING' | 'JSON'>('STRING');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [search, setSearch] = useState('');

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(API);
      if (res.ok) {
        const data = await res.json();
        const map: Record<string, string> = {};
        // Response is a list of {key, value, source, ...} dicts
        if (Array.isArray(data)) {
          for (const c of data) map[c.key || c.config_key] = c.value || c.config_value || '';
        } else if (Array.isArray(data.configs)) {
          for (const c of data.configs) map[c.key || c.config_key] = c.value || c.config_value || '';
        } else if (typeof data === 'object') {
          Object.assign(map, data);
        }
        setConfig(map);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Look up the policy definition for a key so we can write the right value_type
  const findPolicy = (key: string) => {
    for (const g of POLICY_GROUPS) {
      const p = g.policies.find(p => p.key === key);
      if (p) return p;
    }
    return null;
  };

  const startEdit = (key: string) => {
    setEditing(key);
    setEditValue(config[key] || '');
    setEditType(findPolicy(key)?.type || 'STRING');
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const res = await authFetch(`${API}/${editing}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: editValue, value_type: editType }),
      });
      if (res.ok) {
        setConfig(prev => ({ ...prev, [editing!]: editValue }));
        showToast(`Updated ${editing}`, true);
        setEditing(null);
      } else {
        const e = await res.json();
        showToast(e.detail || 'Update failed', false);
      }
    } catch { showToast('Update failed', false); }
  };

  const cancelEdit = () => { setEditing(null); setEditValue(''); };

  const filteredGroups = search
    ? POLICY_GROUPS.map(g => ({
        ...g,
        policies: g.policies.filter(p =>
          p.label.toLowerCase().includes(search.toLowerCase()) ||
          p.key.toLowerCase().includes(search.toLowerCase()) ||
          p.description.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(g => g.policies.length > 0)
    : POLICY_GROUPS;

  if (loading) return <PageLayout title="Client Policy Configuration" subtitle="All policies are per-organization. Changes take effect immediately."><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading configuration...</div></PageLayout>;

  return (
    <PageLayout title="Client Policy Configuration" subtitle="All policies are per-organization. Changes take effect immediately. Previous values are audit-logged.">
      <FilterBar filters={[
        { key: 'search', label: 'policies', type: 'search', value: search, onChange: setSearch, width: 300, placeholder: 'Search policies...' },
      ]} />

      {filteredGroups.map(group => {
        const columns: Column[] = [
          { key: 'label', label: 'Policy', width: '28%', render: (_: any, row: any) => (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--navy-900)' }}>{row.label}</div>
              <div style={{ fontSize: 10, color: 'var(--text-hint)', fontFamily: 'monospace' }}>{row.key}</div>
            </div>
          )},
          { key: 'description', label: 'Description', width: '35%', render: (v: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{v}</span> },
          { key: 'value', label: 'Value', align: 'right', width: '20%', render: (_: any, row: any) => {
            const value = config[row.key] || '';
            const isEditing = editing === row.key;
            const displayValue = row.unit === '$' ? `$${Number(value).toLocaleString()}` : row.unit === '%' ? `${(Number(value) * (Number(value) < 1 ? 100 : 1)).toFixed(1)}%` : value + (row.unit ? ` ${row.unit}` : '');

            if (isEditing) {
              return (
                <input value={editValue} onChange={e => setEditValue(e.target.value)}
                  style={{ ...inputStyle, textAlign: 'right', width: 140 }} autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
              );
            }
            return (
              <span style={{ fontWeight: 600, color: 'var(--navy-800)', cursor: 'pointer' }} onClick={() => startEdit(row.key)} title="Click to edit">
                {displayValue || <span style={{ color: 'var(--text-hint)' }}>not set</span>}
              </span>
            );
          }},
          { key: 'action', label: 'Action', align: 'center', width: '17%', render: (_: any, row: any) => {
            const isEditing = editing === row.key;
            if (isEditing) {
              return (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  <ActionButton label="Save" onClick={saveEdit} variant="success" size="sm" />
                  <ActionButton label="Cancel" onClick={cancelEdit} variant="secondary" size="sm" />
                </div>
              );
            }
            return <ActionButton label="Edit" onClick={() => startEdit(row.key)} variant="ghost" size="sm" />;
          }},
        ];

        return (
          <div key={group.header} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, padding: '6px 0', borderBottom: '2px solid var(--border-subtle)' }}>
              {group.header}
            </div>
            <DataTable columns={columns} rows={group.policies} rowKey="key" />
          </div>
        );
      })}

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000,
          padding: '12px 20px', borderRadius: 8,
          background: toast.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          color: toast.ok ? 'var(--positive)' : 'var(--negative)',
          border: `1px solid ${toast.ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
          fontSize: 13, fontWeight: 600,
        }}>
          {toast.msg}
        </div>
      )}
    </PageLayout>
  );
}
